/**
 * Campaigns (design section 11, nodes vvOmk + mQOr3): the pure parts shared by the Worker (worker/src/campaigns.js,
 * campaign-act.js) and the dashboard: the message template, the action page's design (theme presets, fonts, contrast),
 * slugs and status labels. No DOM, no fetch.
 */

// ---------- the message template ("Dear Ald. {last name}, … {supporter’s reason} … {supporter name}, {street}")

export interface MessageTemplate { subject: string; body: string }
export interface MessageValues { lastName: string; ward: number | string; name: string; reason?: string | null }

/**
 * Fills the template. {street} is always dropped (with the ", " before it): the supporter's address never leaves their
 * browser (owner's privacy rule), so the message can't carry it. An empty reason drops its placeholder and the space
 * before it. Unknown {placeholders} are left as written.
 */
export function renderMessage(t: MessageTemplate, v: MessageValues): MessageTemplate {
  const reason = String(v.reason ?? "").trim();
  const fill = (s: string) => String(s ?? "")
    .replace(/,?[ \t]*\{street\}/gi, "")
    .replace(/[ \t]*\{supporter[’']s reason\}/gi, reason ? ` ${reason}` : "")
    .replace(/\{last name\}/gi, v.lastName)
    .replace(/\{ward\}/gi, String(v.ward))
    .replace(/\{supporter name\}/gi, v.name)
    .replace(/[ \t]+\n/g, "\n")
    .trim();
  return { subject: fill(t.subject), body: fill(t.body) };
}

/** What the supporter sees above the reason box: the body up to {supporter’s reason} (the design's preview). */
export function previewBody(body: string, v: Omit<MessageValues, "reason" | "name"> & { name?: string }): string {
  const cut = String(body ?? "").split(/\{supporter[’']s reason\}/i)[0] ?? "";
  return renderMessage({ subject: "", body: cut }, { ...v, name: v.name ?? "", reason: "" }).body;
}

// ---------- the action page's design

export interface PageTheme { preset: string; bg: string; text: string; accent: string; card: string }
export interface PageLink { label: string; url: string }
export interface PageDesign {
  logo: { name: string; size: number; dataUrl: string } | null;
  theme: PageTheme;
  fonts: { heading: string; body: string };
  footer: { text: string; links: PageLink[] };
}

/** The design's five presets (Coalition · Paper · Night · Civic · Lake): background, text, buttons and links, cards. */
export const PRESETS: Record<string, Omit<PageTheme, "preset">> = {
  Coalition: { bg: "#F2EFE6", text: "#10251C", accent: "#1F6B4A", card: "#FFFFFF" },
  Paper: { bg: "#FFFFFF", text: "#111111", accent: "#111111", card: "#FFFFFF" },
  Night: { bg: "#14171A", text: "#F3F1EA", accent: "#E8C468", card: "#1E2226" },
  Civic: { bg: "#FBF6EF", text: "#1A1220", accent: "#E3342F", card: "#FFFFFF" },
  Lake: { bg: "#EEF5F6", text: "#0D2A30", accent: "#0E6E7E", card: "#FFFFFF" },
};
export const PRESET_NAMES = Object.keys(PRESETS);

/** Fonts a page may use (loaded from Google Fonts on the public page). */
export const FONTS = ["Fraunces", "Inter", "Instrument Serif", "Source Serif 4", "Playfair Display", "Merriweather", "DM Sans", "Work Sans", "IBM Plex Sans", "Libre Franklin"];

export const defaultDesign = (): PageDesign => ({
  logo: null,
  theme: { preset: "Coalition", ...PRESETS.Coalition! },
  fonts: { heading: "Fraunces", body: "Inter" },
  footer: { text: "", links: [] },
});

const HEX = /^#[0-9A-Fa-f]{6}$/;
const LOGO = /^data:image\/(svg\+xml|png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/;
export const LOGO_MAX_BYTES = 200 * 1024;
const safeUrl = (u: unknown) => { try { const x = new URL(String(u)); return x.protocol === "https:" || x.protocol === "http:" || x.protocol === "mailto:" ? x.toString() : null; } catch { return null; } };

/** A design from untrusted JSON: every field checked, anything invalid falls back to the default. */
export function cleanDesign(raw: unknown): PageDesign {
  const d = defaultDesign();
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, any>;
  const th = r.theme || {};
  for (const k of ["bg", "text", "accent", "card"] as const) if (typeof th[k] === "string" && HEX.test(th[k])) d.theme[k] = th[k].toUpperCase();
  if (typeof th.preset === "string" && (PRESET_NAMES.includes(th.preset) || th.preset === "Custom")) d.theme.preset = th.preset;
  if (FONTS.includes(r.fonts?.heading)) d.fonts.heading = r.fonts.heading;
  if (FONTS.includes(r.fonts?.body)) d.fonts.body = r.fonts.body;
  if (typeof r.footer?.text === "string") d.footer.text = r.footer.text.slice(0, 500);
  if (Array.isArray(r.footer?.links)) {
    d.footer.links = r.footer.links.slice(0, 6).map((l: any) => ({ label: String(l?.label ?? "").trim().slice(0, 40), url: safeUrl(l?.url) ?? "" }))
      .filter((l: PageLink) => l.label && l.url);
  }
  const logo = r.logo;
  if (logo && typeof logo.dataUrl === "string" && LOGO.test(logo.dataUrl) && logo.dataUrl.length <= Math.ceil(LOGO_MAX_BYTES * 4 / 3) + 40) {
    d.logo = { name: String(logo.name ?? "logo").slice(0, 80), size: Math.max(0, Math.min(LOGO_MAX_BYTES, Number(logo.size) || 0)), dataUrl: logo.dataUrl };
  }
  return d;
}

/** WCAG contrast ratio of two #RRGGBB colors (1–21). */
export function contrast(a: string, b: string): number {
  const lum = (hex: string) => {
    const c = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255).map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
    return 0.2126 * c[0]! + 0.7152 * c[1]! + 0.0722 * c[2]!;
  };
  const [x, y] = [lum(a), lum(b)].sort((p, q) => q - p);
  return (x! + 0.05) / (y! + 0.05);
}

/** The button's text color: white or the page's text color, whichever reads better on the button. */
export const buttonText = (t: PageTheme) => (contrast("#FFFFFF", t.accent) >= contrast(t.text, t.accent) ? "#FFFFFF" : t.text);

/** "Text 14.0:1 · Button text 6.4:1" (the Theme card's check), and whether both pass AA for body text (4.5:1). */
export function contrastLine(t: PageTheme): { text: number; button: number; ok: boolean } {
  const text = contrast(t.text, t.bg), button = contrast(buttonText(t), t.accent);
  return { text, button, ok: text >= 4.5 && button >= 4.5 };
}

/** The Google Fonts stylesheet for a design's fonts. */
export const fontsHref = (f: PageDesign["fonts"]) =>
  `https://fonts.googleapis.com/css2?${[...new Set([f.heading, f.body])].map((n) => `family=${encodeURIComponent(n).replace(/%20/g, "+")}:wght@400;500;600;700`).join("&")}&display=swap`;

// ---------- slugs and labels

export const slugify = (s: string) => String(s ?? "").toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "")
  .replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48).replace(/-+$/, "") || "campaign";
export const SLUG = /^[a-z0-9](?:[a-z0-9-]{0,46}[a-z0-9])?$/;

export type CampaignStatus = "draft" | "running" | "paused" | "ended";
export type Outcome = "passed" | "held" | "failed" | null;

const SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "2026-10-14" → "Oct 14". */
export const shortDate = (iso: string | null | undefined) => {
  if (!iso) return "";
  const d = new Date(/T/.test(iso) ? iso : `${iso}T12:00:00Z`);
  return Number.isNaN(d.getTime()) ? "" : `${SHORT[d.getUTCMonth()]} ${d.getUTCDate()}`;
};

/** "Running · day 9" · "Draft" · "Ended · passed" · "Paused", and its chip tone. */
export function statusLabel(c: { status: CampaignStatus; outcome?: Outcome; startedAt?: string | null }, now = Date.now()): { label: string; tone: "positive" | "neutral" } {
  if (c.status === "running") {
    const day = c.startedAt ? Math.max(1, Math.floor((now - Date.parse(c.startedAt)) / 86_400_000) + 1) : 1;
    return { label: `Running · day ${day}`, tone: "positive" };
  }
  if (c.status === "ended") return { label: c.outcome ? `Ended · ${c.outcome}` : "Ended", tone: "neutral" };
  return { label: c.status === "paused" ? "Paused" : "Draft", tone: "neutral" };
}

/** The line under a campaign's name: "SO2026-0026554 · vote Oct 14", "O2025-0018782 · passed Jul 22". */
export function itemLine(c: { item?: string | null; recordNumber?: string | null; voteDate?: string | null; status: CampaignStatus; outcome?: Outcome; endedAt?: string | null }): string {
  const parts = [c.item || c.recordNumber || ""];
  if (c.status === "ended" && c.outcome && c.endedAt) parts.push(`${c.outcome} ${shortDate(c.endedAt)}`);
  else if (c.voteDate) parts.push(`vote ${shortDate(c.voteDate)}`);
  return parts.filter(Boolean).join(" · ");
}

/** "Gilbert Villegas" → "GV"; "Ald. Gilbert Villegas" → "GV". */
export const initials = (name: string) => String(name ?? "").replace(/^Ald\.\s*/i, "").split(/\s+/).filter((w) => /^[A-Za-z]/.test(w)).map((w) => w[0]!.toUpperCase()).filter((_, i, a) => i === 0 || i === a.length - 1).join("");
